import { router } from '@inertiajs/svelte'

const ANNOUNCE_DELAY_MS = 100
const CLEAR_DELAY_MS = 1000

interface StartedVisit {
  id: string
  preserveScroll: unknown
  preserveState: unknown
  only: string[]
}

export function announceInertiaNavigations(): () => void {
  const region = document.createElement('div')
  region.className = 'visually-hidden'
  region.setAttribute('aria-live', 'polite')
  region.setAttribute('aria-atomic', 'true')
  document.body.append(region)

  let currentComponent: string | null = null
  let startedVisit: StartedVisit | null = null
  let announceTimer: ReturnType<typeof setTimeout> | undefined

  // Emptied first, so a title equal to the previous one is still announced. Emptied again afterwards,
  // so a stale title doesn't linger in the page's text.
  function announce(): void {
    clearTimeout(announceTimer)
    region.textContent = ''
    announceTimer = setTimeout(() => {
      region.textContent = document.title
      announceTimer = setTimeout(() => (region.textContent = ''), CLEAR_DELAY_MS)
    }, ANNOUNCE_DELAY_MS)
  }

  // Inertia only keeps the state when the component stays the same, and defaults `preserveState` to
  // true for every non-GET visit, so a form redirecting elsewhere is still a page change.
  function staysInPlace(visit: StartedVisit, sameComponent: boolean): boolean {
    return (
      Boolean(visit.preserveScroll) ||
      visit.only.length > 0 ||
      (Boolean(visit.preserveState) && sameComponent)
    )
  }

  const removeStartListener = router.on('start', (event) => {
    const { id, preserveScroll, preserveState, only } = event.detail.visit
    startedVisit = { id, preserveScroll, preserveState, only }
  })

  const removeNavigateListener = router.on('navigate', (event) => {
    const { page, visitId } = event.detail
    const previousComponent = currentComponent
    currentComponent = page.component

    if (previousComponent === null) {
      return
    }

    const visit = startedVisit?.id === visitId ? startedVisit : null
    if (visit && staysInPlace(visit, page.component === previousComponent)) {
      return
    }

    announce()

    // Inertia's popstate handler is the only one firing `navigate` without a visit id.
    const historyVisit = visitId === undefined
    if (!historyVisit && !window.location.hash) {
      const main = document.getElementById('main')
      ;(main?.querySelector<HTMLElement>('[autofocus]') ?? main)?.focus({ preventScroll: true })
    }
  })

  return () => {
    removeStartListener()
    removeNavigateListener()
    clearTimeout(announceTimer)
    region.remove()
  }
}
